import { Fragment, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './corporate-breadcrumb.css';

type BreadcrumbItem = {
  label: string;
  path: string;
};

const SEGMENT_LABELS: Record<string, string> = {
  catalog: 'Catálogo',
  create: 'Criar',
  docs: 'Documentação',
  settings: 'Configurações',
  api: 'APIs',
  search: 'Pesquisa',
  home: 'Início',
  user: 'Usuário',
  groups: 'Grupos',
  system: 'Sistema',
  component: 'Componente',
  resource: 'Recurso',
  domain: 'Domínio',
  template: 'Template',
};

const normalizeLabel = (segment: string): string => {
  const decoded = decodeURIComponent(segment);

  if (SEGMENT_LABELS[decoded]) {
    return SEGMENT_LABELS[decoded];
  }

  return decoded
    .replace(/[-_]+/g, ' ')
    .replace(/\b\p{L}/gu, character => character.toLocaleUpperCase('pt-BR'));
};

const buildBreadcrumbItems = (pathname: string): BreadcrumbItem[] => {
  const segments = pathname.split('/').filter(Boolean);
  const items: BreadcrumbItem[] = [{ label: 'Início', path: '/' }];

  segments.forEach((segment, index) => {
    items.push({
      label: normalizeLabel(segment),
      path: `/${segments.slice(0, index + 1).join('/')}`,
    });
  });

  return items;
};

export const CorporateBreadcrumb = () => {
  const { pathname } = useLocation();

  const items = useMemo(() => buildBreadcrumbItems(pathname), [pathname]);

  return (
    <nav
      className="arx-corporate-breadcrumb"
      aria-label="Navegação estrutural"
      data-aranox-component="corporate-breadcrumb"
    >
      <ol className="arx-corporate-breadcrumb__list">
        {items.map((item, index) => {
          const isCurrent = index === items.length - 1;

          return (
            <Fragment key={`${item.path}-${index}`}>
              <li className="arx-corporate-breadcrumb__item">
                {isCurrent ? (
                  <span
                    className="arx-corporate-breadcrumb__current"
                    aria-current="page"
                    title={item.label}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    className="arx-corporate-breadcrumb__link"
                    to={item.path}
                    title={`Ir para ${item.label}`}
                  >
                    {item.label}
                  </Link>
                )}
              </li>

              {!isCurrent && (
                <li
                  className="arx-corporate-breadcrumb__separator"
                  aria-hidden="true"
                >
                  /
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};
